import React, { useState, useCallback } from "react";
import { Search, Loader2, ChevronLeft, ChevronRight, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { BACKEND_URL } from "@/config/api";
import AdminTopMenuBar from "@/components/AdminTopMenuBar";

const getAuthToken = () => localStorage.getItem("accessToken");

export default function AdminReferralsPage() {
  const [referrals, setReferrals] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterTitle, setFilterTitle] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [sortField, setSortField] = useState("sent_at");
  const [sortDir, setSortDir] = useState("desc");

  const PAGE_SIZE = 50;

  const fetchReferrals = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: pageNum, page_size: PAGE_SIZE });
      if (filterTitle) params.append("manuscript_id", filterTitle);
      if (filterUser) params.append("originating_user_id", filterUser);
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals?${params}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setReferrals(data.referrals);
      setTotal(data.total);
      setHasMore(data.has_more);
      setPage(pageNum);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterTitle, filterUser]);

  const handleDelete = async (referralId) => {
    if (!window.confirm("Delete this referral record?")) return;
    setDeletingId(referralId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/referrals/${referralId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      setReferrals((prev) => prev.filter((r) => r.referral_id !== referralId));
      setTotal((prev) => prev - 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedReferrals = React.useMemo(() => {
    if (!referrals) return null;
    return [...referrals].sort((a, b) => {
      const aVal = a[sortField] || "";
      const bVal = b[sortField] || "";
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [referrals, sortField, sortDir]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "America/Denver" }) + " MST";
    } catch { return iso; }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-amber-400" /> : <ArrowDown className="w-3 h-3 text-amber-400" />;
  };

  const columns = [
    { key: "sent_at", label: "Date/Time Sent" },
    { key: "originating_user_fullname", label: "Originating User" },
    { key: "originating_user_email", label: "User Email" },
    { key: "book_title", label: "Book Title" },
    { key: "referred_email", label: "Referred Email" },
    { key: "status", label: "Status" },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <AdminTopMenuBar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-6" data-testid="referrals-page-title">Referrals</h1>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Filter by Book Title / Manuscript ID</label>
            <input
              type="text"
              value={filterTitle}
              onChange={(e) => setFilterTitle(e.target.value)}
              placeholder="Manuscript ID"
              data-testid="referral-filter-title"
              className="px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-200 outline-none focus:border-amber-500 w-64"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Filter by User ID</label>
            <input
              type="text"
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              placeholder="User ID"
              data-testid="referral-filter-user"
              className="px-3 py-2 rounded-lg text-sm bg-slate-900 border border-slate-700 text-slate-200 outline-none focus:border-amber-500 w-64"
            />
          </div>
          <button
            onClick={() => fetchReferrals(1)}
            disabled={loading}
            data-testid="referral-search-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {loading ? "Loading..." : "Load Referrals"}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-800/50 text-red-300 text-sm" data-testid="referral-error">
            {error}
          </div>
        )}

        {sortedReferrals && (
          <>
            <div className="mb-2 text-xs text-slate-400" data-testid="referral-count">{total} referral{total !== 1 ? "s" : ""} found</div>
            <div className="overflow-x-auto rounded-lg border border-slate-700">
              <table className="w-full text-sm" data-testid="referrals-table">
                <thead className="bg-slate-800/80 text-slate-400 text-xs uppercase">
                  <tr>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-2 text-left cursor-pointer select-none hover:text-slate-200 transition-colors"
                        onClick={() => handleSort(col.key)}
                        data-testid={`sort-${col.key}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label} <SortIcon field={col.key} />
                        </span>
                      </th>
                    ))}
                    <th className="px-4 py-2 text-center w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {sortedReferrals.map((r, i) => (
                    <tr key={r.referral_id || i} className="hover:bg-slate-800/40" data-testid={`referral-row-${i}`}>
                      <td className="px-4 py-2 text-slate-300 whitespace-nowrap">{formatDate(r.sent_at)}</td>
                      <td className="px-4 py-2 text-white">{r.originating_user_fullname || "—"}</td>
                      <td className="px-4 py-2 text-slate-300">{r.originating_user_email}</td>
                      <td className="px-4 py-2 text-white">{r.book_title}</td>
                      <td className="px-4 py-2 text-slate-300">{r.referred_email}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${r.status === "sent" ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                          {r.status || "unknown"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => handleDelete(r.referral_id)}
                          disabled={deletingId === r.referral_id}
                          data-testid={`delete-referral-${i}`}
                          className="p-1.5 rounded hover:bg-red-900/40 text-slate-500 hover:text-red-400 transition-colors disabled:opacity-30"
                          title="Delete referral"
                        >
                          {deletingId === r.referral_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sortedReferrals.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No referrals found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 text-sm text-slate-400">
              <span>Page {page} of {Math.ceil(total / PAGE_SIZE) || 1}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchReferrals(page - 1)}
                  disabled={page <= 1 || loading}
                  data-testid="referral-prev-page"
                  className="flex items-center gap-1 px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <button
                  onClick={() => fetchReferrals(page + 1)}
                  disabled={!hasMore || loading}
                  data-testid="referral-next-page"
                  className="flex items-center gap-1 px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
